import axios, { AxiosInstance } from "axios";
import queryString from "query-string";
import fileDownload from "js-file-download";
import { IDataContextProvider } from "@contexts/data/types";
import { IHttpError } from "@type";

const axiosInstances: { [key: string]: AxiosInstance } = {};

// Cache instances by API URL as requested (avoid constantly creating)
const createAxiosInstance = (apiUrl: string) => {
  if (Object.prototype.hasOwnProperty.call(axiosInstances, apiUrl)) {
    return axiosInstances[apiUrl];
  }

  const axiosInstance = axios.create();

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const customError: IHttpError = {
        ...error,
        message: error.response?.data?.message,
        statusCode: error.response?.status,
      };

      return Promise.reject(customError);
    },
  );

  axiosInstances[apiUrl] = axiosInstance;

  return axiosInstance;
};

const JsonServer = (
  apiUrl: string,
  httpClient?: AxiosInstance,
): IDataContextProvider => {
  const axiosInstance = httpClient || createAxiosInstance(apiUrl);

  return {
    authenticate: async (authToken: string) => {
      axiosInstance.defaults.headers.common = {
        Authorization: `Bearer ${authToken}`,
      };
    },

    getList: async ({
      resource,
      hasPagination = true,
      pagination = { current: 1, pageSize: 10 },
    }) => {
      const url = `${apiUrl}/${resource}`;
      const { current = 1, pageSize = 10 } = pagination ?? {};

      const query: {
        _start?: number;
        _end?: number;
        _sort?: string;
        _order?: string;
      } = hasPagination
        ? {
            _start: (current - 1) * pageSize,
            _end: current * pageSize,
          }
        : {};

      const queryFilters = {}; //generateFilter(filters); //TODO

      const { data, headers } = await axiosInstance.get(
        `${url}?${queryString.stringify(query)}&${queryString.stringify(
          queryFilters,
        )}`,
      );

      const total = +headers["x-total-count"];

      return {
        data,
        total,
      };
    },

    getOne: async ({ resource, id }) => {
      const url = `${apiUrl}/${resource}/${id}`;
      const { data } = await axiosInstance.get(url);
      return { data };
    },

    create: async ({ resource, variables }) => {
      const url = `${apiUrl}/${resource}`;

      const { data } = await axiosInstance.post(url, variables);

      return {
        data,
      };
    },

    update: async ({ resource, id, variables }) => {
      const url = `${apiUrl}/${resource}/${id}`;

      const { data } = await axiosInstance.patch(url, variables);

      return {
        data,
      };
    },

    delete: async ({ resource, id }) => {
      const url = `${apiUrl}/${resource}/${id}`;

      const { data } = await axiosInstance.delete(url);

      return {
        data,
      };
    },

    getApiUrl: () => {
      return apiUrl;
    },

    custom: async ({ url, method, filters, sort, payload, query, headers }) => {
      let requestUrl = url;

      // if (sort) {
      //   const generatedSort = generateSort(sort);
      //   if (generatedSort) {
      //     const { _sort, _order } = generatedSort;
      //     const sortQuery = {
      //       _sort: _sort.join(","),
      //       _order: _order.join(","),
      //     };
      //     requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      //   }
      // }

      // if (filters) {
      //   const filterQuery = generateFilter(filters);
      //   requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
      // }

      if (query) {
        requestUrl = `${requestUrl}?${queryString.stringify(query)}`;
      }

      let axiosResponse;
      switch (method) {
        case "put":
        case "post":
        case "patch":
          if (!headers) {
            headers = {};
          }

          if (!headers["Content-Type"]) {
            headers["Content-Type"] = "application/json";
          }

          axiosResponse = await axiosInstance[method](requestUrl, payload, {
            headers,
          });
          break;
        case "delete":
          axiosResponse = await axiosInstance.delete(requestUrl, { headers });
          break;
        default:
          axiosResponse = await axiosInstance.get(requestUrl, { headers });
          break;
      }

      const { data } = axiosResponse;

      return Promise.resolve({ data });
    },

    upload: async ({ resource, variables, headers }: any) => {
      const url = `${apiUrl}/${resource}`;

      const form_data = new FormData();
      if (variables) {
        for (const field of Object.keys(variables))
          form_data.append(field, variables[field]);
      }

      const { data } = await axiosInstance.post(url, form_data, {
        headers: {
          ...(headers || {}),
          "Content-Type": "multipart/form-data",
          Accept: "*/*",
        },
      });

      return {
        data,
      };
    },

    //TODO:not tested yet
    download: async ({ resource, variables, headers }: any) => {
      const url = `${apiUrl}/${resource}`;

      return axiosInstance
        .post(url, variables, {
          headers,
          responseType: "blob",
        })
        .then((response) => {
          if (response.status != 200) {
            return { data: null };
          }

          let filename = "";
          const disposition = response.headers["content-disposition"];
          if (disposition) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, "");
            }
          }
          fileDownload(
            response.data,
            filename,
            response.headers["content-type"],
          );

          return {
            data: response.data,
          };
        });
    },
  };
};

export default JsonServer;
